/**
 * Operator Layout
 *
 * Mobile-first layout for operator portal.
 * OperatorHeader (56px sticky) at top, OperatorTabBar (fixed bottom),
 * profile BottomSheet with org info + personalize link + logout,
 * max-w-lg centered content area.
 */

import { useState, useMemo, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  LogOut,
  Briefcase,
  Clock,
  User,
  MapPin,
  Hash,
  Sparkles,
  ChevronRight,
} from 'lucide-react';
import { OperatorHeader } from './components/OperatorHeader';
import { OperatorTabBar } from './components/OperatorTabBar';
import { BottomSheet } from './components/BottomSheet';
import { PreferencesSheet } from './components/PreferencesSheet';
import { NotificationsSheet } from './components/NotificationsSheet';
import { WelcomeScreen } from './components/WelcomeScreen';
import { PreShiftBriefingCard } from './components/PreShiftBriefingCard';
import { CoachFAB } from './components/CoachFAB';
import { getInitials, getFirstName } from './utils';
import { useOperatorDashboard, useOperatorTraining } from '../../hooks/useOperatorPortal';
import { usePreferences } from './hooks/usePreferences';

const WELCOME_STORAGE_KEY = 'op_seenWelcome';

/** Delay between closing profile sheet and opening preferences sheet (ms).
 *  BottomSheet exit animation is 300ms; 350ms avoids overlap on the boundary frame. */
const PREFS_SHEET_DELAY_MS = 350;

export function OperatorLayout() {
  const { user, logout } = useAuth0();
  const [profileOpen, setProfileOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [prefsSheetOpen, setPrefsSheetOpen] = useState(false);

  // Dashboard data for org info display in profile sheet
  const { data: dashboard } = useOperatorDashboard();

  // Training data for PreferencesSheet sopNames
  const { data: trainingItems } = useOperatorTraining();
  const sopNames = useMemo(
    () => [
      ...new Set(
        (trainingItems ?? [])
          .map((item) => item.sopName)
          .filter((name): name is string => name !== null && name !== undefined)
      ),
    ],
    [trainingItems]
  );

  // Preferences save handler
  const hasCompletedTraining = trainingItems?.some((item) => item.status === 'completed') ?? false;
  const { savePreferences } = usePreferences(hasCompletedTraining);

  // Welcome screen -- shown once, persisted in localStorage
  const [showWelcome, setShowWelcome] = useState(() => {
    try {
      return localStorage.getItem(WELCOME_STORAGE_KEY) !== 'true';
    } catch (e) {
      console.warn('Failed to read welcome state:', e);
      return false; // If localStorage is unavailable, skip welcome
    }
  });

  const handleDismissWelcome = () => {
    setShowWelcome(false);
    try {
      localStorage.setItem(WELCOME_STORAGE_KEY, 'true');
    } catch {
      // localStorage unavailable -- welcome won't persist but that's OK
    }
  };

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleOpenPreferences = useCallback(() => {
    // Close profile sheet first, then open preferences after the exit animation
    setProfileOpen(false);
    setTimeout(() => {
      setPrefsSheetOpen(true);
    }, PREFS_SHEET_DELAY_MS);
  }, []);

  const firstName = getFirstName(user?.name, user?.given_name);
  const operator = dashboard?.operator ?? null;

  // Determine if any org info rows should be shown
  const hasOrgInfo = operator && (
    operator.departmentName !== null ||
    operator.shiftName !== null ||
    operator.positionName !== null ||
    operator.zoneName !== null
  );

  // Build org info rows (only non-null values)
  const orgInfoRows = useMemo(() => {
    if (!operator) return [];
    const rows: Array<{ icon: typeof Briefcase; label: string; value: string }> = [];
    if (operator.departmentName) {
      rows.push({ icon: Briefcase, label: 'Department', value: operator.departmentName });
    }
    if (operator.shiftName) {
      rows.push({ icon: Clock, label: 'Shift', value: operator.shiftName });
    }
    if (operator.positionName) {
      rows.push({ icon: User, label: 'Position', value: operator.positionName });
    }
    if (operator.zoneName) {
      rows.push({ icon: MapPin, label: 'Zone', value: operator.zoneName });
    }
    return rows;
  }, [operator]);

  return (
    <div className="min-h-screen bg-op-surface">
      <OperatorHeader
        onBellClick={() => setNotificationsOpen(true)}
        onAvatarClick={() => setProfileOpen(true)}
      />

      <main className="mx-auto max-w-lg px-4 pb-24 pt-4">
        <PreShiftBriefingCard />
        <Outlet />
      </main>

      <CoachFAB coachEnabled={dashboard?.coachEnabled} />

      <OperatorTabBar />

      {/* Welcome screen overlay -- shown once on first visit */}
      {showWelcome && (
        <WelcomeScreen
          firstName={firstName}
          onDismiss={handleDismissWelcome}
        />
      )}

      {/* Notifications BottomSheet */}
      <NotificationsSheet
        isOpen={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
      />

      {/* Profile BottomSheet */}
      <BottomSheet
        isOpen={profileOpen}
        onClose={() => setProfileOpen(false)}
        title="Profile"
        snapTo="full"
      >
        <div className="flex flex-col gap-6 pb-4">
          {/* Section A: Identity */}
          <div className="flex flex-col items-center">
            {/* Avatar */}
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-guild-green text-lg font-semibold text-white">
              {getInitials(user?.name)}
            </div>

            {/* Name */}
            <p className="mt-3 text-lg font-semibold text-op-text-primary">
              {user?.name || 'Operator'}
            </p>

            {/* Email */}
            <p className="mt-0.5 text-sm text-op-text-secondary">
              {user?.email || ''}
            </p>

            {/* Employee ID badge */}
            {operator?.employeeId && (
              <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-op-muted px-3 py-1">
                <Hash size={14} strokeWidth={1.5} className="text-op-text-tertiary" />
                <span className="text-xs font-medium text-op-text-secondary">
                  {operator.employeeId}
                </span>
              </div>
            )}
          </div>

          {/* Divider */}
          <hr className="border-op-muted" />

          {/* Section B: Organization Info (conditional) */}
          {hasOrgInfo && (
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wider text-op-text-tertiary px-1">
                Your Info
              </p>
              <div className="rounded-xl bg-op-muted/50 divide-y divide-op-muted">
                {orgInfoRows.map((row) => (
                  <div
                    key={row.label}
                    className="flex items-center gap-3 px-4 py-3"
                  >
                    <row.icon
                      size={18}
                      strokeWidth={1.5}
                      className="flex-shrink-0 text-op-text-tertiary"
                    />
                    <span className="text-xs font-medium text-op-text-tertiary w-20 flex-shrink-0">
                      {row.label}
                    </span>
                    <span className="text-sm font-medium text-op-text-primary truncate">
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Section C: Personalize Training link */}
          {operator && (
            <button
              type="button"
              onClick={handleOpenPreferences}
              className="flex min-h-[48px] w-full items-center gap-3 rounded-xl bg-op-muted/50 px-4 py-3 text-left transition-colors duration-150 active:bg-gray-100 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
            >
              <Sparkles
                size={18}
                strokeWidth={1.5}
                className="flex-shrink-0 text-guild-green"
              />
              <span className="flex-1 text-sm font-semibold text-op-text-primary">
                Personalize Training
              </span>
              <ChevronRight
                size={18}
                strokeWidth={1.5}
                className="flex-shrink-0 text-op-text-tertiary"
              />
            </button>
          )}

          {/* Section D: Logout */}
          <button
            type="button"
            onClick={handleLogout}
            className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-red-50 px-6 py-3 text-sm font-semibold text-red-700 active:bg-red-100 transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
          >
            <LogOut size={18} strokeWidth={1.5} />
            Log out
          </button>
        </div>
      </BottomSheet>

      {/* Preferences BottomSheet (opened from profile sheet) */}
      <PreferencesSheet
        isOpen={prefsSheetOpen}
        onClose={() => setPrefsSheetOpen(false)}
        sopNames={sopNames}
        onSave={(prefs) => {
          savePreferences(prefs);
          setPrefsSheetOpen(false);
        }}
      />
    </div>
  );
}
